import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  CustomTooltipModule,
  DialogPresets,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  IPatient,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  Firestore,
  isSameRef,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { compact, first, isString } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { HeidiWidgetService } from '../../../services/heidi-widget.service';
import {
  HeidiAccountSettingsComponent,
  IHeidiAccountSettingsData,
} from '../../heidi/heidi-account-settings/heidi-account-settings.component';
import { AiNotesMarketingTooltipComponent } from '../ai-notes-marketing-tooltip/ai-notes-marketing-tooltip.component';

enum HeidiAccountType {
  Staffer = 'staffer',
  Practice = 'practice',
}

interface IHeidiAccount {
  name: string;
  email: string;
  type: HeidiAccountType;
  ref: DocumentReference<IStaffer | IPractice>;
}

@Component({
  selector: 'pr-ai-notes-button',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDivider,
    MatProgressSpinner,
    CustomTooltipModule,
    CdkOverlayOrigin,
    AiNotesMarketingTooltipComponent,
  ],
  templateUrl: './ai-notes-button.component.html',
  styleUrl: './ai-notes-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiNotesButtonComponent {
  private _organisation = inject(OrganisationService);
  private _dialog = inject(MatDialog);
  private _practice$: Observable<WithRef<IPractice>>;
  private _staffer$: Observable<WithRef<IStaffer>>;

  heidiService = inject(HeidiWidgetService);
  patient = input.required<WithRef<IPatient>>();
  noteCreated = output<string>();

  heidiAccounts$: Observable<IHeidiAccount[]>;
  hasAnyAccount$: Observable<boolean>;
  showAccountSelection$: Observable<boolean>;

  tooltipPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  constructor() {
    this._practice$ = this._organisation.practice$.pipe(filterUndefined());
    this._staffer$ = this._organisation.staffer$.pipe(
      filterUndefined(),
      switchMap((staffer) => Firestore.doc$(staffer.ref)),
      distinctUntilChanged((stafferA, stafferB) =>
        this._isSameHeidiSettings(stafferA, stafferB)
      )
    );

    this._practice$
      .pipe(
        take(1),
        map((practice) => !!practice.metadata?.heidi?.testMode),
        takeUntilDestroyed()
      )
      .subscribe((testMode) => void this.heidiService.preload(testMode));

    effect(() => {
      const heidiNote = this.heidiService.noteData();
      if (heidiNote && isString(heidiNote)) {
        this.noteCreated.emit(heidiNote);
        this.heidiService.clearNoteData();
      }
    });

    this.heidiAccounts$ = combineLatest([this._practice$, this._staffer$]).pipe(
      map(([practice, staffer]) =>
        this._getAvailableAccounts(practice, staffer)
      )
    );

    this.hasAnyAccount$ = this.heidiAccounts$.pipe(
      map((accounts) => accounts.length > 0)
    );

    this.showAccountSelection$ = combineLatest([
      this.heidiAccounts$,
      this._staffer$,
    ]).pipe(
      map(([accounts, staffer]) => {
        const useStafferAsDefault =
          !!staffer.metadata?.heidi?.useAsDefault &&
          !!staffer.metadata.heidi.email;
        return accounts.length > 1 && !useStafferAsDefault;
      })
    );
  }

  async openDefaultAccount(): Promise<void> {
    const accounts = await snapshot(this.heidiAccounts$);
    if (!accounts.length) {
      await this.openAccountSettings();
      return;
    }

    await this.openHeidiWidget(this._resolveDefaultAccount(accounts));
  }

  async openHeidiWidget(account?: IHeidiAccount): Promise<void> {
    if (account) {
      await this.heidiService.open(
        this.patient(),
        account.email,
        account.ref.id
      );
    }
  }

  async openAccountSettings(): Promise<void> {
    this._dialog.open<HeidiAccountSettingsComponent, IHeidiAccountSettingsData>(
      HeidiAccountSettingsComponent,
      DialogPresets.medium({
        data: {
          practice: await snapshot(this._practice$),
          staffer: await snapshot(this._staffer$),
        },
      })
    );
  }

  private _getAvailableAccounts(
    practice: WithRef<IPractice>,
    staffer: WithRef<IStaffer>
  ): IHeidiAccount[] {
    return compact([
      staffer.metadata?.heidi?.email && {
        name: staffer.user.name,
        email: staffer.metadata.heidi.email,
        type: HeidiAccountType.Staffer,
        ref: staffer.ref,
      },
      practice.metadata?.heidi?.email && {
        name: practice.name,
        email: practice.metadata.heidi.email,
        type: HeidiAccountType.Practice,
        ref: practice.ref,
      },
    ]);
  }

  private _resolveDefaultAccount(
    accounts: IHeidiAccount[]
  ): IHeidiAccount | undefined {
    if (accounts.length === 1) {
      return first(accounts);
    }

    const stafferAccount = accounts.find(
      (account) => account.type === HeidiAccountType.Staffer
    );
    return stafferAccount || first(accounts);
  }

  private _isSameHeidiSettings(
    stafferA: WithRef<IStaffer>,
    stafferB: WithRef<IStaffer>
  ): boolean {
    const heidiSettingsA = stafferA.metadata?.heidi;
    const heidiSettingsB = stafferB.metadata?.heidi;

    return (
      isSameRef(stafferA, stafferB) &&
      heidiSettingsA?.email === heidiSettingsB?.email &&
      heidiSettingsA?.useAsDefault === heidiSettingsB?.useAsDefault
    );
  }
}
